import { useMemo } from 'react';
import { getItemWithExpiration } from '../../lib/utils';
import {
    useAutomationHeldQuery,
    useAutomationLimitQuery,
    useAutomationRulesQuery,
    useGiftCandidatesQuery,
    useGiftChargePolicyQuery,
    useGiftChargesQuery,
    useGiftHistoryQuery,
    useGiftPointAccountQuery,
    useGiftProductsQuery,
    useGiftSegmentsQuery,
    useGiftSettingsQuery,
    useGiftSummaryQuery,
    useGiftTopProductsQuery,
    useTrainerBudgetPermissionsQuery,
} from '../../services/useGifticonQueryService';
import {
    AutomationTiming,
    ChargePolicy,
    GiftCatalogProduct,
    GiftHistoryItem,
    GiftHistoryQuery,
    GiftHistorySummary,
    GiftSummary,
    ReregStatus,
    SegmentKey as ApiSegmentKey,
    TopProduct,
} from '../../types/gifticon';
import type {
    AutomationRule,
    Candidate,
    ChargeRecord,
    ChargeStatus,
    HistoryRow,
    Segment,
    SegmentKey,
    TrainerPerformance,
} from './GifticonGiftPage';
import { resolveHistoryStatusLabel } from './historyStatus';

// 서버 응답(AS-BUILT) → 페이지 UI 모델 매핑 레이어.
// 데이터가 아직 없으면 각 필드는 null — 페이지는 목업 초기값을 유지하다가 도착 시 시딩한다.

const API_TO_UI_SEGMENT: Record<ApiSegmentKey, SegmentKey> = {
    birthday: 'birthday',
    rereg: 'rereg',
    absent: 'absent',
    milestone: 'milestone',
    newMember: 'newMember',
    attendance: 'excellent', // UI는 'excellent'(출석 우수) 키 사용
    anniversary: 'anniversary',
};
const UI_TO_API_SEGMENT: Record<SegmentKey, ApiSegmentKey> = {
    birthday: 'birthday',
    rereg: 'rereg',
    absent: 'absent',
    milestone: 'milestone',
    newMember: 'newMember',
    excellent: 'attendance',
    anniversary: 'anniversary',
};

const SEGMENT_TONES: Record<SegmentKey, string> = {
    birthday: '#3D73DD',
    rereg: '#00A793',
    absent: '#FF8777',
    milestone: '#9E76F3',
    newMember: '#17B5CB',
    excellent: '#B45309',
    anniversary: '#E35BA2',
};

// v3: 트리거 = 세그먼트 키 7종 (타이밍보드와 동일 라벨). 구 enum 라벨은 마이그레이션 전 표시 호환용.
const TRIGGER_LABELS: Record<string, string> = {
    birthday: '생일',
    milestone: '재등록 임박',
    absent: '미출석·이탈 위험',
    rereg: '최근 재등록',
    newMember: '신규 등록',
    attendance: '출석 우수',
    anniversary: '센터 등록 기념일',
    // 구(v2) enum — 마이그레이션 전 저장 규칙 표시용
    BIRTHDAY: '회원 생일',
    ANNIVERSARY: '센터 등록 기념일',
    SESSION_MILESTONE: '수업 회차 달성',
    REREGISTERED: '재등록 완료',
    ABSENT_DAYS: '미출석 지속',
    NEW_MEMBER: '신규 등록',
    TRAINER_RENEWALS: '재등록 유치',
    TRAINER_REVENUE: '매출 달성',
    TRAINER_JOURNAL_RATE: '일지 작성률',
};

const TIMING_LABELS: Record<AutomationTiming, string> = {
    DAY_9AM: '당일 오전 9시',
    ON_TRIGGER: '조건 충족 시',
    AFTER_PAYMENT_1H: '결제 1시간 후',
    JOIN_DAY: '등록 당일',
    MONTH_END: '월말 정산 시',
};

const HELD_REASON_LABELS: Record<string, string> = {
    INSUFFICIENT_BALANCE: '잔액 부족',
    ISSUE_FAILED: '발급 실패',
    LIMIT_EXCEEDED: '한도 초과',
};

const CHARGE_STATUS_LABELS: Record<string, ChargeStatus> = {
    PENDING_CONFIRM: '입금 확인 중',
    CONFIRMED: '충전 완료',
    CANCELED: '취소',
};

// 값 타입은 HistoryRow['route']보다 넓게(string) 잡는다 — MEMBER_SEND 등 신규 route는
// 아직 GifticonGiftPage.tsx의 좁은 라벨 유니온에 없어 원값을 그대로 노출해야 하기 때문.
const ROUTE_LABELS: Record<string, string> = {
    DIRECT: '직접 발송',
    DASHBOARD: '대시보드 추천',
    AUTOMATION: '자동 규칙',
    CAMPAIGN: '단체 캠페인',
    TRAINER_SEND: '강사 발송',
    MEMBER_SEND: '회원 발송',
};

const formatDateTime = (iso: string): string => {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return iso;
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const hh = String(date.getHours()).padStart(2, '0');
    const mi = String(date.getMinutes()).padStart(2, '0');
    // 올해가 아니면 연도(2자리)를 붙여 과거 기록임을 구분한다
    if (date.getFullYear() !== new Date().getFullYear()) {
        const yy = String(date.getFullYear()).slice(-2);
        return `'${yy}.${mm}.${dd} ${hh}:${mi}`;
    }
    return `${mm}.${dd} ${hh}:${mi}`;
};

const toReregStatus = (status: ReregStatus | null): HistoryRow['reregStatus'] => {
    if (!status) return undefined;
    return status === 'REREGISTERED' ? 'YES' : status;
};

const daysAgoLabel = (iso: string | null): string => {
    if (!iso) return '없음';
    const diff = Math.floor((Date.now() - new Date(iso).getTime()) / (24 * 60 * 60 * 1000));
    return diff <= 0 ? '오늘' : `${diff}일 전`;
};

const mapHistoryItem = (item: GiftHistoryItem): HistoryRow => ({
    id: item.itemId,
    batchId: item.batchId,
    date: formatDateTime(item.sentAt),
    sortAt: item.sentAt,
    receiver: item.receiverName,
    receiverType: item.receiverType === 'TRAINER' ? '강사' : '회원',
    reason: item.reason ?? '',
    // 미지 route는 라벨 매핑 없이 원값 노출 — HistoryRow['route']가 아직 신규 route를 모르므로 캐스팅
    route: (ROUTE_LABELS[item.route] ?? item.route) as HistoryRow['route'],
    product: item.productName,
    // 예약 건은 기존 예약 목록 동작을 유지하고, 그 외에는 서버 계산 상태를 단일 기준으로 사용한다.
    // 새 관리자를 선배포하는 짧은 구간에는 구 백엔드 상태 조합으로 표시한다.
    status: resolveHistoryStatusLabel({
        batchStatus: item.status,
        couponStatus: item.couponStatus,
        displayStatus: item.displayStatus,
    }),
    displayStatus: item.displayStatus,
    payment: item.payment === 'POINT' ? '충전 잔액' : '바로 결제',
    amount: item.amount,
    trainerName: item.trainerName ?? undefined,
    reregStatus: toReregStatus(item.reregStatus),
});

export type CandidateWithServerId = Candidate & { memberId: string };

export interface GifticonServerData {
    pointBalance: number | null;
    unconfirmedAmount: number | null;
    chargeRecords: ChargeRecord[] | null;
    segments: Segment[] | null;
    homeSegmentKeys: SegmentKey[] | null;
    candidates: CandidateWithServerId[] | null;
    trainers: TrainerPerformance[] | null;
    automationRules: AutomationRule[] | null;
    automationLimit: number | null;
    automationUsed: number | null;
    heldAutomation: { id: string; receiver: string; rule: string; product: string; amount: number; reason: string }[] | null;
    historyRows: HistoryRow[] | null;
    historySummary: GiftHistorySummary | null;
    summary: GiftSummary | null;
    topProducts: TopProduct[] | null;
    products: GiftCatalogProduct[] | null;
    chargePolicy: ChargePolicy | null;
    accessToken: string;
    isLoading: boolean;
    isError: boolean;
    refetchAll: () => void;
    historyStatus: { isLoading: boolean; isError: boolean };
    candidatesStatus: { isLoading: boolean; isError: boolean };
    chargePolicyStatus: { isLoading: boolean; isError: boolean };
    productsStatus: { isLoading: boolean; isError: boolean };
}

export const toApiSegmentKey = (key: SegmentKey): ApiSegmentKey => UI_TO_API_SEGMENT[key];

/** 화면 필터 state(한글 라벨) → GET /gifts 쿼리 파라미터 변환 — CSV export·서버 필터 공용 */
export const buildHistoryQuery = (filters: {
    period: 'all' | 'today' | 'week' | 'month' | 'custom';
    startDate: string;
    endDate: string;
    receiverType: string;
    route: string;
    trainerName: string;
    keyword: string;
}): GiftHistoryQuery => {
    const routeEntry = Object.entries(ROUTE_LABELS).find(([, label]) => label === filters.route);
    return {
        period:
            filters.period === 'all' ? 'ALL'
            : filters.period === 'today' ? 'TODAY'
            : filters.period === 'week' ? 'WEEK'
            : filters.period === 'month' ? 'MONTH'
            : 'CUSTOM',
        startDate: filters.period === 'custom' && filters.startDate ? filters.startDate : undefined,
        endDate: filters.period === 'custom' && filters.endDate ? filters.endDate : undefined,
        receiverType: filters.receiverType === '회원' ? 'MEMBER' : filters.receiverType === '강사' ? 'TRAINER' : 'ALL',
        route: (routeEntry?.[0] as GiftHistoryQuery['route']) ?? 'ALL',
        trainerName: filters.trainerName !== '전체' ? filters.trainerName : undefined,
        keyword: filters.keyword.trim() || undefined,
    };
};

// v3: 규칙 에디터가 enum 값을 직접 다루므로 한글 문구 역매핑(toApiTrigger/toApiTiming)과
// 상품 이름 대조(resolveServerProductId)는 제거됐다 (F-16/DX-08 해소).

export const useGifticonServerData = (): GifticonServerData => {
    const accessToken: string = getItemWithExpiration('authorization') ?? '';

    const pointAccount = useGiftPointAccountQuery({ accessToken });
    const charges = useGiftChargesQuery({ accessToken, page: 1, size: 20 });
    const segments = useGiftSegmentsQuery({ accessToken });
    const settings = useGiftSettingsQuery({ accessToken });
    const candidates = useGiftCandidatesQuery({
        accessToken,
        segments: 'birthday,rereg,absent,milestone,newMember,attendance,anniversary',
    });
    const trainers = useTrainerBudgetPermissionsQuery({ accessToken });
    const rules = useAutomationRulesQuery({ accessToken });
    const limit = useAutomationLimitQuery({ accessToken });
    const held = useAutomationHeldQuery({ accessToken });
    const history = useGiftHistoryQuery({ accessToken, query: { period: 'ALL', page: 1, size: 100 } });
    const summary = useGiftSummaryQuery({ accessToken });
    const topProducts = useGiftTopProductsQuery({ accessToken });
    const products = useGiftProductsQuery({ accessToken });
    const chargePolicy = useGiftChargePolicyQuery({ accessToken });

    // 매핑 결과를 서버 데이터 참조 기준으로 고정 — 페이지 시딩 useEffect가 실제 변경 시에만 실행되게 함
    // (매 렌더 새 배열이면 낙관적 로컬 업데이트가 이전 서버 값으로 즉시 되돌아가 깜빡임·입력 불가 발생)
    const mappedChargeRecords = useMemo(
        () =>
            charges.data?.items.map((item): ChargeRecord => ({
                id: item.chargeId,
                requestedAt: formatDateTime(item.requestedAt),
                depositorName: item.depositorName,
                depositAmount: item.depositAmount,
                discountAmount: item.discountAmount,
                grantedAmount: item.grantedAmount,
                status: CHARGE_STATUS_LABELS[item.status] ?? '입금 대기',
            })) ?? null,
        [charges.data],
    );
    const mappedSegments = useMemo(
        () =>
            segments.data?.map((segment): Segment => {
                const key = API_TO_UI_SEGMENT[segment.segmentKey] ?? 'birthday';
                return {
                    key,
                    title: segment.title,
                    description: segment.description,
                    // 기준 설명 표기(UX 기획 §4) — 구서버 응답에는 없는 필드라 빈 값 폴백
                    criteria: segment.criteria ?? '',
                    recurrence: segment.recurrence ?? '',
                    computedAt: segment.computedAt ?? null,
                    count: segment.waitingCount,
                    sentThisMonth: segment.sentThisMonth,
                    budgetUsed: segment.budgetUsed,
                    recommendedGift: segment.recommendedProduct
                        ? `${segment.recommendedProduct.brand} ${segment.recommendedProduct.name}`
                        : '추천 상품 미설정',
                    unitPrice: segment.recommendedProduct?.price ?? 0,
                    // 발송 시 이름 대조 없이 서버 productId를 그대로 쓰기 위해 브리프를 그대로 실어 보낸다
                    recommendedProduct: segment.recommendedProduct
                        ? {
                            id: segment.recommendedProduct.productId,
                            brand: segment.recommendedProduct.brand,
                            name: segment.recommendedProduct.name,
                            price: segment.recommendedProduct.price,
                        }
                        : null,
                    tone: SEGMENT_TONES[key],
                    priority: segment.priority === 'HIGH' ? '높음' : segment.priority === 'MEDIUM' ? '보통' : '낮음',
                };
            }) ?? null,
        [segments.data],
    );
    const mappedHomeSegmentKeys = useMemo(
        () => settings.data?.homeSegmentKeys.map((key) => API_TO_UI_SEGMENT[key]).filter(Boolean) ?? null,
        [settings.data],
    );
    const mappedCandidates = useMemo(
        () =>
            candidates.data?.map((candidate, index): CandidateWithServerId => ({
                id: index + 1,
                memberId: candidate.memberId,
                name: candidate.name,
                segment: API_TO_UI_SEGMENT[candidate.segmentKey] ?? 'birthday',
                detail: candidate.reason,
                coach: candidate.trainerName,
                lastGift: daysAgoLabel(candidate.lastGiftAt),
            })) ?? null,
        [candidates.data],
    );
    const mappedTrainers = useMemo(
        () =>
            trainers.data?.map((trainer): TrainerPerformance => ({
                id: trainer.trainerId,
                name: trainer.name,
                role: trainer.role,
                renewals: 0,
                revenue: 0,
                journalRate: 0,
                rating: 0,
                budgetEnabled: trainer.enabled,
                monthlyLimit: trainer.monthlyLimit,
                usedThisMonth: trainer.usedThisMonth,
            })) ?? null,
        [trainers.data],
    );
    const mappedAutomationRules = useMemo(
        () =>
            rules.data?.map((rule): AutomationRule => ({
                id: rule.ruleId,
                name: rule.name,
                target: rule.target === 'TRAINER' ? '강사' : '회원',
                condition: `${TRIGGER_LABELS[rule.trigger.type] ?? rule.trigger.type}${
                    rule.trigger.threshold ? ` ${rule.trigger.threshold}` : ''
                }`,
                timing: TIMING_LABELS[rule.timing] ?? rule.timing,
                product: rule.product ? `${rule.product.brand} ${rule.product.name}` : '상품 미설정',
                price: rule.product?.price ?? 0,
                estimate: rule.monthlyEstimateCount,
                enabled: rule.enabled,
                recent: rule.lastExecution
                    ? `${formatDateTime(rule.lastExecution.at)} · ${rule.lastExecution.count}명 · ${rule.lastExecution.amount.toLocaleString()}P`
                    : '아직 실행된 적 없음',
                // 에디터가 기존 값을 정확히 로드하기 위한 원값(F-16) — 라벨 문자열 역매핑을 쓰지 않는다
                triggerType: rule.trigger.type,
                timingValue: rule.timing,
                productId: rule.product?.productId ?? null,
                message: rule.message,
            })) ?? null,
        [rules.data],
    );
    const mappedHeld = useMemo(
        () =>
            held.data?.map((item) => ({
                id: item.heldId,
                receiver: item.receiverName ?? '-',
                rule: item.sourceName ?? '-',
                product: item.product,
                amount: item.amount,
                reason: HELD_REASON_LABELS[item.reason] ?? item.reason,
            })) ?? null,
        [held.data],
    );
    const mappedHistoryRows = useMemo(() => history.data?.items.map(mapHistoryItem) ?? null, [history.data]);

    const queries = [
        pointAccount,
        charges,
        segments,
        settings,
        candidates,
        trainers,
        rules,
        limit,
        held,
        history,
        summary,
        topProducts,
        products,
        chargePolicy,
    ];

    return {
        accessToken,
        pointBalance: pointAccount.data?.balance ?? null,
        unconfirmedAmount: pointAccount.data?.unconfirmedAmount ?? null,
        chargeRecords: mappedChargeRecords,
        segments: mappedSegments,
        homeSegmentKeys: mappedHomeSegmentKeys,
        candidates: mappedCandidates,
        trainers: mappedTrainers,
        automationRules: mappedAutomationRules,
        automationLimit: limit.data?.monthlyLimit ?? null,
        automationUsed: limit.data?.usedThisMonth ?? null,
        heldAutomation: mappedHeld,
        historyRows: mappedHistoryRows,
        historySummary: history.data?.summary ?? null,
        summary: summary.data ?? null,
        topProducts: topProducts.data?.topProducts ?? null,
        products: products.data ?? null,
        chargePolicy: chargePolicy.data ?? null,
        isLoading: queries.some((query) => query.isLoading),
        isError: queries.some((query) => query.isError),
        refetchAll: () => {
            queries.forEach((query) => {
                query.refetch();
            });
        },
        historyStatus: { isLoading: history.isLoading, isError: history.isError },
        candidatesStatus: { isLoading: candidates.isLoading, isError: candidates.isError },
        chargePolicyStatus: { isLoading: chargePolicy.isLoading, isError: chargePolicy.isError },
        productsStatus: { isLoading: products.isLoading, isError: products.isError },
    };
};
